import { useEffect, useMemo, useState } from 'react';
import type {
  MailcomAccountsResponse,
  MailcomReadinessResponse,
  MailcomSummaryResponse,
} from '../../api/endpoints.js';
import type { useApiQuery } from '../../api/hooks.js';
import { useDelayedFlag } from '../../hooks/useDelayedFlag.js';
import {
  Badge,
  Banner,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Icon,
  LoadingState,
  StatCard,
} from '../../ui/index.js';
import { describeWarning, fmtDate } from './shared.js';

const STAT_TIPS: Record<string, string> = {
  total: 'Tüm parent mail hesaplarının toplam sayısı (status fark etmeksizin).',
  active: 'status=active olan parent hesapları. Runtime trafiği bunlar üzerinden döner.',
  disabled: 'status=disabled — kalıcı devre dışı parent hesaplar: operatör kapatması, abuse-hard-lock veya ömür-boyu alias limiti (0 canlı alias + MAX_LIMIT terminal).',
  loginFailed: 'status=login_failed — son oturum denemelerinde kimlik doğrulama hatası alan hesaplar.',
  aliasLimit: 'Aktif olup 9 hedefin ALTINDA (1-8 canlı alias) tutan parent sayısı — tam kapasiteye ulaşamamış / kısmi stoklu hesaplar. Canlı alias sayısından hesaplanır (available+leased+reserved). Bu hesaplar satışa devam eder; kart yalnızca görünürlük sağlar.',
  renewing: 'Şu an yenilenmekte olan parent sayısı (yalnızca çalışan/running renewal job — anlık geçici durum, biten işte otomatik düşer).',
  available: 'Şu an satışa hazır alias toplamı (status=available, herhangi bir parent).',
  reserved: "Reservation pipeline'ında, henüz lease edilmemiş alias.",
  leased: 'Aktif sipariş tarafından lease edilmiş alias.',
  used: 'Kullanılmış / tüketilmiş alias.',
};

// R-D214 — V1-parity status taxonomy union. Mirror of MailcomPage's
// StatusFilter so stat-card clicks can drive any of the 11 buckets.
type AccountStatusFilter =
  | 'all'
  | 'active'
  | 'draining'
  | 'suspended'
  | 'login_failed'
  | 'disabled'
  | 'alias_limit_reached'
  | 'renewal_failed'
  | 'renewal_in_progress'
  | 'upstream_unavailable'
  | 'blocked'
  | 'bootstrapping';

interface MailcomSummaryProps {
  readonly summaryQuery: ReturnType<typeof useApiQuery<MailcomSummaryResponse>>;
  readonly accountsQuery: ReturnType<typeof useApiQuery<MailcomAccountsResponse>>;
  readonly readinessQuery: ReturnType<typeof useApiQuery<MailcomReadinessResponse>>;
  readonly onRefresh: () => void;
  /** B.1 — clicking a status stat card sets the parent-list status filter (V1 parity). */
  readonly activeStatusFilter?: AccountStatusFilter;
  readonly onSelectStatus?: (status: AccountStatusFilter) => void;
  /** Provider display label (e.g. 'mail.com', 'gmx.com') for provider-correct copy. */
  readonly providerLabel?: string;
  /**
   * R-UI-MAILCOM-V1-ADAPT — alias-lifecycle cards now live on the Inventory
   * tab. When a user is on the Parent tab and clicks one of the *parent-status*
   * cards we filter the table; alias-count cards are no longer shown here.
   */
  readonly onScrollToInventory?: () => void;
}

export function MailcomSummary({
  summaryQuery,
  accountsQuery,
  readinessQuery,
  onRefresh,
  activeStatusFilter,
  onSelectStatus,
  providerLabel = 'mail.com',
}: MailcomSummaryProps): JSX.Element {
  // Re-derive whenever EITHER source settles a fresh payload. We key the memo
  // on the hook `version` counters (bumped only on a changed response) plus the
  // current `kind`, NOT on the query object identity — that prevents both a
  // stale closure (a fresh poll always re-derives) and needless churn (an
  // identical poll response keeps version stable, so cards don't repaint).
  // no-silent-stale: the displayed numbers always reflect the freshest fetch.
  const derived = useMemo(() => {
    const summary = summaryQuery.kind === 'ok' ? summaryQuery.data : null;
    const accountsResp =
      accountsQuery.kind === 'ok' ? accountsQuery.data ?? null : null;
    const byStatus = (summary?.accounts?.byStatus ?? {}) as Readonly<Record<string, number>>;
    // R-D214 — paginated path: prefer the per-request breakdown blocks
    // (they reflect the filtered universe). Non-paginated path: fall
    // back to the summary endpoint's byStatus block.
    const statusBreakdown = accountsResp?.statusBreakdown ?? null;
    const suspendedReasonBreakdown =
      accountsResp?.suspendedReasonBreakdown ?? null;
    return {
      total: summary?.accounts?.total ?? null,
      active: statusBreakdown?.active ?? byStatus.active ?? 0,
      disabled: statusBreakdown?.disabled ?? byStatus.disabled ?? 0,
      loginFailed: statusBreakdown?.login_failed ?? byStatus.login_failed ?? 0,
      // FIX-1 (R-PARENT-CLASS, requirement a): "Yenileniyor" is a TRANSIENT
      // in-flight count sourced from the renewal-JOB row, never from
      // provider_accounts.status_detail (which used to be re-stamped to
      // 'renewal_in_progress' every tick and stuck at 148/148). Paginated
      // path: accountsResp.runningRenewals; non-paginated/summary path:
      // summary.renewal.renewing (both now count
      // provider_renewal_jobs.status='running').
      renewingInProgress:
        accountsResp?.runningRenewals
        ?? summary?.renewal?.renewing
        ?? null,
      // FIX-1 — "Yenileme Hatası" is V1-exact PERSISTENT status, keyed on
      // suspended_reason='renewal_failed' (survives status_detail churn).
      renewalFailed: suspendedReasonBreakdown?.renewal_failed ?? null,
      // R-ALIASLIMIT-CARD (operator-directed) — the "Alias Limiti" card is now
      // COUNT-BASED: ACTIVE parents holding 1-8 live aliases (below the 9-target
      // but not empty/dead). Sourced from the summary's `belowTarget` count
      // (direct live-alias SQL), NOT the status-flag suspendedReasonBreakdown
      // (which stayed empty because forward-only classification + the
      // login_failed storm never populated it). Visibility only — these parents
      // stay active and keep selling.
      aliasLimitReached: summary?.accounts?.belowTarget ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    summaryQuery.kind,
    summaryQuery.version,
    accountsQuery.kind,
    accountsQuery.version,
  ]);

  const refreshing = summaryQuery.refreshing || accountsQuery.refreshing;

  // Commit-on-idle (RC2, 2026-06-10 flicker fix): the poll reloads summary +
  // accounts together but they settle in two waves, so painting per wave can
  // mix a fresh summary with a stale accounts page (or vice versa) for one
  // frame. The PAINTED stats therefore live in state and are committed from
  // `derived` ONLY when no background revalidation is in flight — exactly one
  // visual update per poll cycle, mixed intermediate frames never reach the
  // DOM. First load is unaffected: both `refreshing` flags are false then
  // (the hook uses kind='loading' instead), so the initial numbers still
  // appear progressively as each query lands.
  const [stats, setStats] = useState(derived);
  useEffect(() => {
    if (!refreshing) setStats(derived);
  }, [refreshing, derived]);

  // RC1 (primary flicker root): the 'güncelleniyor…' chip used to mount/
  // unmount per poll (~50-300ms fetch → millisecond DOM blink + header layout
  // shift; .card-header-actions had no reserved box). It is now ALWAYS
  // mounted — visibility is pure opacity, debounced by useDelayedFlag (a fast
  // poll never shows it; once shown it holds ≥600ms). no-silent-stale: a slow
  // refresh is still surfaced to the operator.
  const showRefreshing = useDelayedFlag(refreshing);

  // First-load only: blank with a LoadingState while we have NOTHING to show.
  // Background polls keep prior numbers visible (stale-while-revalidate) and
  // surface a subtle 'refreshing' chip instead — the panel never blanks/
  // remounts on a poll, killing the "page reloads every 10s" flicker.
  const firstLoad =
    (summaryQuery.kind === 'loading' || accountsQuery.kind === 'loading') &&
    stats.total === null;
  const errored = summaryQuery.kind === 'error' && accountsQuery.kind === 'error';

  const summary = summaryQuery.kind === 'ok' ? summaryQuery.data : null;
  const activity = summary?.activity;

  return (
    <Card flush data-testid="mailcom-v1-stats-section">
      <CardHeader
        title="Parent Durumu"
        actions={
          /* RC1 — ALWAYS mounted; never return undefined here. An empty
             actions slot would drop .card-header-actions from the DOM and
             re-introduce the mount/unmount blink + header layout shift. */
          <span
            className="t-tertiary t-xs"
            data-testid="mailcom-stats-refreshing"
            title="Canlı yenileme"
            aria-hidden={!showRefreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              opacity: showRefreshing ? 1 : 0,
              transition: 'opacity .15s ease',
            }}
          >
            <Icon.Clock size={11} />
            güncelleniyor…
          </span>
        }
      />
      <CardBody>
        {firstLoad ? <LoadingState label="İstatistikler yükleniyor…" /> : null}
        {errored ? (
          <ErrorState
            reason={(summaryQuery as { reason?: string }).reason ?? 'unknown'}
            onRetry={onRefresh}
          />
        ) : null}

        <ReadinessBlock query={readinessQuery} providerLabel={providerLabel} />

        <div className="kpi-grid" data-testid="mailcom-v1-stats-grid" style={{ marginTop: 12 }}>
          <Stat label="Toplam Parent" value={stats.total} hint={STAT_TIPS.total} testId="stat-card-all" onClick={onSelectStatus ? () => onSelectStatus('all') : undefined} active={activeStatusFilter === 'all'} />
          <Stat label="Aktif" value={stats.active} hint={STAT_TIPS.active} tone="success" testId="stat-card-active" onClick={onSelectStatus ? () => onSelectStatus('active') : undefined} active={activeStatusFilter === 'active'} />
          <Stat label="Devre Dışı" value={stats.disabled} hint={STAT_TIPS.disabled} tone="danger" testId="stat-card-disabled" onClick={onSelectStatus ? () => onSelectStatus('disabled') : undefined} active={activeStatusFilter === 'disabled'} />
          <Stat label="Giriş Hatası" value={stats.loginFailed} hint={STAT_TIPS.loginFailed} tone="danger" testId="stat-card-login_failed" onClick={onSelectStatus ? () => onSelectStatus('login_failed') : undefined} active={activeStatusFilter === 'login_failed'} />
          {/* R-D214 — V1 taxonomy stat cards. Each clicks-through to filter. */}
          <Stat label="Yenileniyor" value={stats.renewingInProgress} hint={STAT_TIPS.renewing} tone="warn" testId="stat-card-renewal_in_progress" onClick={onSelectStatus ? () => onSelectStatus('renewal_in_progress') : undefined} active={activeStatusFilter === 'renewal_in_progress'} />
          <Stat label="Yenileme Hatası" value={stats.renewalFailed} hint={STAT_TIPS.renewing} tone="danger" testId="stat-card-renewal_failed" onClick={onSelectStatus ? () => onSelectStatus('renewal_failed') : undefined} active={activeStatusFilter === 'renewal_failed'} />
          {/* R-ALIASLIMIT-CARD (operator-directed) — count-based: ACTIVE parents
              holding 1-8 live aliases (below the 9-target). Not click-filterable
              (it's a live-count metric, not a status bucket). */}
          <Stat label="Alias Limiti" value={stats.aliasLimitReached} hint={STAT_TIPS.aliasLimit} tone="warn" testId="stat-card-alias_limit_reached" />
        </div>

        {activity ? (
          <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
            }}
            data-testid="mailcom-activity-footer"
          >
            <ActivityCell label="Son başarılı fetch" value={fmtDate(activity.lastSuccessfulFetchAt)} icon={<Icon.Clock size={12} />} />
            <ActivityCell label="Son oturum kontrolü" value={fmtDate(activity.lastSessionCheckedAt)} icon={<Icon.Clock size={12} />} />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * R-UI-MAILCOM-V1-ADAPT — Alias lifecycle stat cards, rendered on the
 * Inventory tab (split out of MailcomSummary). Source data is the same
 * MailcomSummaryResponse; we just show a different slice.
 */
export function AliasLifecycleStats({
  summaryQuery,
}: {
  readonly summaryQuery: ReturnType<typeof useApiQuery<MailcomSummaryResponse>>;
}): JSX.Element | null {
  const summary = summaryQuery.kind === 'ok' ? summaryQuery.data : null;
  if (summary === null) return null;
  const a = summary.aliases;
  return (
    <Card flush data-testid="mailcom-alias-lifecycle-stats">
      <CardHeader title="Alias Yaşam Döngüsü" />
      <CardBody>
        <div className="kpi-grid" data-testid="alias-lifecycle-grid">
          <Stat label="Satışa Hazır" value={a.available} hint={STAT_TIPS.available} tone="success" testId="stat-card-available" />
          <Stat label="Rezerve" value={a.reserved} hint={STAT_TIPS.reserved} testId="stat-card-reserved" />
          <Stat label="Siparişte" value={a.leased} hint={STAT_TIPS.leased} testId="stat-card-leased" />
          <Stat label="Tüketilmiş" value={a.used} hint={STAT_TIPS.used} testId="stat-card-used" />
        </div>
      </CardBody>
    </Card>
  );
}

function ReadinessBlock({
  query,
  providerLabel = 'mail.com',
}: {
  readonly query: ReturnType<typeof useApiQuery<MailcomReadinessResponse>>;
  readonly providerLabel?: string;
}): JSX.Element | null {
  if (query.kind === 'loading' || query.kind === 'idle') return null;
  if (query.kind === 'error') {
    return <Banner tone="warn">Provider hazırlık durumu yüklenemedi. Yeniden deneyin.</Banner>;
  }
  if (query.kind === 'auth_required') return null;
  const data = query.data;
  if (!data) {
    return <Banner tone="warn">Provider hazırlık durumu yanıtı boş. Yenileyip tekrar deneyin.</Banner>;
  }
  if (data.ready) {
    return (
      <Banner tone="success" testId="readiness-ok">
        {providerLabel} gerçek çalışmaya hazır: parent + credential + satışa uygun alias mevcut, kasa devrede.
      </Banner>
    );
  }
  return (
    <Banner tone="danger" title="Bu provider gerçek çalışmaya hazır değil." testId="readiness-not-ready">
      <ul style={{ margin: '6px 0 0', paddingLeft: 22 }}>
        {(data.warnings ?? []).map((w) => (
          <li key={w}>{describeWarning(w)}</li>
        ))}
      </ul>
    </Banner>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
  onClick,
  active,
  testId,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly hint?: string;
  readonly tone?: 'success' | 'warn' | 'danger';
  readonly onClick?: () => void;
  readonly active?: boolean;
  readonly testId?: string;
}): JSX.Element {
  const display = value === null ? '—' : value.toLocaleString('tr-TR');
  const trend = tone === 'success' ? 'up' : tone === 'danger' ? 'down' : undefined;
  return (
    <StatCard
      label={label}
      value={display}
      meta={hint ? <span title={hint}>{hint.length > 40 ? hint.slice(0, 40) + '…' : hint}</span> : undefined}
      trend={trend}
      testId={testId ?? `v1-stat-${label}`}
      onClick={onClick}
      active={active}
    />
  );
}

function ActivityCell({ label, value, icon }: { readonly label: string; readonly value: string; readonly icon: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="t-tertiary t-xs" style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
        {icon}
        {label}
      </div>
      <div className="t-mono t-small">{value}</div>
    </div>
  );
}

export function OAuthConfigPanel({
  query,
  onRefresh,
}: {
  readonly query: ReturnType<typeof useApiQuery<MailcomReadinessResponse>>;
  readonly onRefresh: () => void;
}): JSX.Element {
  const data = query.kind === 'ok' ? query.data : null;
  // R38 (D-153) — V1-bridge account-id reconciliation removed (per-account
  // vault is the V2 truth, D-149). No bridge row / banners here anymore.
  const anyEnvMissing =
    data && (!data.oauthClientSecretPresent || !data.oauthClientIdPresent || !data.upstreamBaseUrlPresent);

  return (
    <Card flush data-testid="mailcom-oauth-config">
      <CardHeader title="OAuth Yapılandırması" />
      <CardBody>
        <p className="t-tertiary t-small" style={{ marginTop: 0, marginBottom: 12 }}>
          Bu değerler runtime env üzerinden yönetilir. <strong>Admin panel secret değerini görmez ve göstermez.</strong>{' '}
          Eksik bir env varsa sunucudaki <code>infra/test/.env.test</code> güncellenmeli ve API + worker yeniden başlatılmalıdır.
        </p>

        {query.kind === 'loading' ? <LoadingState /> : null}
        {query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={onRefresh} /> : null}
        {data ? (
          <table data-testid="mailcom-oauth-env-table" className="tbl">
            <thead>
              <tr>
                <th>Env değişkeni</th>
                <th>Durum</th>
                <th>Operatör notu</th>
              </tr>
            </thead>
            <tbody>
              <EnvRow
                name="PROVIDER_MAILCOM_OAUTH_CLIENT_ID"
                present={data.oauthClientIdPresent}
                note="OAuth2 token uç noktasında HTTP Basic kullanıcı adı olarak gönderilir."
              />
              <EnvRow
                name="PROVIDER_MAILCOM_OAUTH_CLIENT_SECRET"
                present={data.oauthClientSecretPresent}
                note="OAuth2 token uç noktasında HTTP Basic şifre olarak gönderilir. Admin panele girilmez."
              />
              <EnvRow
                name="PROVIDER_MAILCOM_OAUTH_AUTH_METHOD"
                present={Boolean(data.oauthAuthMethodPresent)}
                note="OAuth2 kimlik metodu. Tanımsızsa varsayılan basic kabul edilir."
              />
              <EnvRow
                name="PROVIDER_MAILCOM_UPSTREAM_BASE_URL"
                present={data.upstreamBaseUrlPresent}
                note="mail.com upstream API base URL. Eksikse provider runtime hiç çalışamaz."
              />
            </tbody>
          </table>
        ) : null}

        {anyEnvMissing ? (
          <div style={{ marginTop: 12 }}>
            <Banner tone="warn" title="Eksik env tespit edildi.">
              Admin UI burada secret değerini almaz; sunucu env dosyasına ilgili satırı eklemek ve API + worker
              servislerini yeniden başlatmak operatörün sorumluluğundadır.
            </Banner>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function EnvRow({ name, present, note }: { readonly name: string; readonly present: boolean; readonly note: string }): JSX.Element {
  return (
    <tr>
      <td className="t-mono t-xs">{name}</td>
      <td>
        {present ? <Badge tone="success" dot>tanımlı</Badge> : <Badge tone="danger" dot>tanımsız</Badge>}
      </td>
      <td style={{ fontSize: 12.5 }}>{note}</td>
    </tr>
  );
}
